import React, { useEffect, useMemo, useState } from "react";
import PageHeader from "../layout/PageHeader";
import api from "../../api/client";

const scoreToLabel = (score) => {
  const value = Number(score || 0);
  if (value >= 85) return { label: "Very Strong", color: "#16a34a", bg: "#dcfce7" };
  if (value >= 70) return { label: "Strong", color: "#2563eb", bg: "#dbeafe" };
  if (value >= 55) return { label: "Good", color: "#7c3aed", bg: "#ede9fe" };
  if (value >= 40) return { label: "Fair", color: "#d97706", bg: "#fef3c7" };
  if (value >= 25) return { label: "Weak", color: "#dc2626", bg: "#fee2e2" };
  return { label: "Not Recommended", color: "#6b7280", bg: "#f3f4f6" };
};

const ScoreBadge = ({ score }) => {
  const { label, color, bg } = scoreToLabel(score);
  return (
    <span
      style={{
        backgroundColor: bg,
        color,
        padding: "2px 10px",
        borderRadius: "12px",
        fontWeight: 600,
        fontSize: "0.8rem",
        letterSpacing: "0.02em",
      }}
    >
      {label}
    </span>
  );
};

// Evaluation Criteria checklist (Agency.evaluationCriteriaEnabled) — replaces
// the numeric ScoreBadge with an "X of Y items met" summary. Candidates from
// agencies without the flag never have criteriaTotalCount set (stays 0), so
// hasCriteriaChecklist() below is the switch between the two UIs.
const hasCriteriaChecklist = (item) => Number(item?.criteriaTotalCount || 0) > 0;

const CriteriaBadge = ({ metCount, totalCount }) => {
  const total = Number(totalCount || 0);
  const met = Number(metCount || 0);
  const bg = total > 0 && met === total ? "#dcfce7" : "#dbeafe";
  const color = total > 0 && met === total ? "#16a34a" : "#2563eb";
  return (
    <span
      style={{
        backgroundColor: bg,
        color,
        padding: "2px 10px",
        borderRadius: "12px",
        fontWeight: 600,
        fontSize: "0.8rem",
        letterSpacing: "0.02em",
      }}
    >
      {met} of {total} items met
    </span>
  );
};

const RatingBadge = ({ item }) =>
  hasCriteriaChecklist(item) ? (
    <CriteriaBadge metCount={item?.criteriaMetCount} totalCount={item?.criteriaTotalCount} />
  ) : (
    <ScoreBadge score={item?.interview?.score ?? 0} />
  );

// Pre-video-interview recording-consent decline flag (2026-07-28) — mirrors
// AiHiringCandidates.js's VideoConsentDeclineBadge. "pending"/"consented"
// render nothing.
const VIDEO_CONSENT_DECLINE_LABELS = {
  declined_rescheduled: "🎥 Declined video — rescheduled",
  declined_switched_to_phone: "🎥 Declined video — switched to phone",
};

const VideoConsentDeclineBadge = ({ videoConsentStatus }) => {
  const label = VIDEO_CONSENT_DECLINE_LABELS[videoConsentStatus];
  if (!label) return null;
  return (
    <span
      style={{
        backgroundColor: "#fef3c7",
        color: "#d97706",
        padding: "2px 10px",
        borderRadius: "12px",
        fontWeight: 600,
        fontSize: "0.75rem",
        letterSpacing: "0.02em",
        display: "inline-block",
        marginTop: 4,
      }}
    >
      {label}
    </span>
  );
};

// Video interview end-reason flag (2026-07-28) — mirrors
// AiHiringCandidates.js's VideoEndedByBadge. "candidate_confirmed" (the
// pre-existing "Finish Interview" button) and "natural_completion" render
// nothing — those are the expected/normal outcomes.
const VIDEO_ENDED_BY_LABELS = {
  candidate_ended_early: { text: "⏹ Candidate ended interview early", bg: "#fef3c7", fg: "#b45309" },
  timeout: { text: "⏱ Interview timed out", bg: "#f1f5f9", fg: "#475569" },
  disconnected: { text: "⚠ Candidate disconnected", bg: "#fef3c7", fg: "#b45309" },
};

const VideoEndedByBadge = ({ endedBy }) => {
  const info = VIDEO_ENDED_BY_LABELS[endedBy];
  if (!info) return null;
  return (
    <span
      style={{
        backgroundColor: info.bg,
        color: info.fg,
        padding: "2px 10px",
        borderRadius: "12px",
        fontWeight: 600,
        fontSize: "0.75rem",
        letterSpacing: "0.02em",
        display: "inline-block",
        marginTop: 4,
      }}
    >
      {info.text}
    </span>
  );
};

const HiringReviewQueue = (props) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selected, setSelected] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [removingIds, setRemovingIds] = useState({});
  const [toast, setToast] = useState(null);

  const loadQueue = async () => {
    try {
      setLoading(true);
      const res = await api.get("/admin/review-queue");
      const list = Array.isArray(res?.data) ? res.data : res?.data?.items || [];
      setItems(list);
      setError("");
    } catch (err) {
      setItems([]);
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Failed to load review queue.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQueue();
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const id = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(id);
  }, [toast]);

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    const from = fromDate ? new Date(`${fromDate}T00:00:00`) : null;
    const to = toDate ? new Date(`${toDate}T23:59:59`) : null;
    return items.filter((item) => {
      const haystack = [
        item?.name,
        item?.firstName,
        item?.lastName,
        item?.email,
        item?.phone,
        item?.jobTitle,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (q && !haystack.includes(q)) return false;

      const flaggedAt = new Date(
        item?.rejectionReview?.flaggedAt || item?.createdAt || item?.updatedAt || 0,
      );
      if (from && flaggedAt < from) return false;
      if (to && flaggedAt > to) return false;
      return true;
    });
  }, [items, search, fromDate, toDate]);

  const removeRowAfterAction = (candidateId, action) => {
    setRemovingIds((prev) => ({ ...prev, [candidateId]: action }));
    setTimeout(() => {
      setItems((prev) => prev.filter((item) => item.candidateId !== candidateId));
      setRemovingIds((prev) => {
        const copy = { ...prev };
        delete copy[candidateId];
        return copy;
      });
    }, 260);
  };

  const handleAction = async (candidate, action) => {
    const id = candidate?.candidateId;
    if (!id || busyId) return;
    const isConfirm = action === "confirm";
    try {
      setBusyId(id);
      if (isConfirm) {
        await api.post(`/admin/candidates/${id}/confirm-rejection`);
        setToast({ type: "confirm", text: "Rejected and email sent" });
      } else {
        await api.post(`/admin/candidates/${id}/override-rejection`);
        setToast({ type: "override", text: "Moved to approved and orientation scheduled" });
      }
      removeRowAfterAction(id, action);
      if (selected?.candidateId === id) setSelected(null);
    } catch (err) {
      setToast({
        type: "error",
        text:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Action failed.",
      });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="page-shell ai-hiring-page review-queue-page">
      <PageHeader
        title="AI Hiring - Final Review"
        subtitle="Recruiting review required before final hiring decision."
        icon={
          <span role="img" aria-label="Review Queue">
            🧾
          </span>
        }
        {...props}
      />

      <div className="ai-hiring-content">
        {error ? <div className="ai-alert ai-alert-error">{error}</div> : null}

        <div className="review-card">
          <div className="review-filters">
            <input
              className="review-input"
              type="text"
              placeholder="Search name, email, phone, role"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <input
              className="review-input"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
            <input
              className="review-input"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
            <button type="button" className="review-reset-btn" onClick={() => { setSearch(""); setFromDate(""); setToDate(""); }}>
              Reset
            </button>
          </div>

          {loading ? (
            <div className="ai-empty">Loading review queue...</div>
          ) : filteredItems.length ? (
            <div className="review-table-wrap">
              <table className="review-table">
                <thead>
                  <tr>
                    <th>Candidate</th>
                    <th>Contact</th>
                    <th>Role</th>
                    <th>Rating</th>
                    <th>Flagged</th>
                    <th>Summary</th>
                    <th>Review Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((item) => {
                    const id = item?.candidateId;
                    const summary = String(
                      item?.interview?.summarySnippet ||
                        "No summary available.",
                    );
                    const flaggedAt = item?.rejectionReview?.reviewedAt || null;
                    return (
                      <tr
                        key={id}
                        className={removingIds[id] ? `review-row-removing review-row-removing-${removingIds[id]}` : ""}
                      >
                        <td>{item?.name || `${item?.firstName || ""} ${item?.lastName || ""}`.trim() || "--"}</td>
                        <td>{item?.phone || "--"}</td>
                        <td>{item?.jobTitle || item?.role || "--"}</td>
                        <td>
                          <RatingBadge item={item} />
                          <div>
                            <VideoConsentDeclineBadge
                              videoConsentStatus={item?.videoConsentStatus}
                            />
                          </div>
                          <div>
                            <VideoEndedByBadge endedBy={item?.interview?.endedBy} />
                          </div>
                        </td>
                        <td>{flaggedAt ? new Date(flaggedAt).toLocaleDateString() : "--"}</td>
                        <td>
                          <button type="button" className="review-link" onClick={() => setSelected(item)}>
                            View
                          </button>
                        </td>
                        <td>
                          <div className="review-actions">
                            <button
                              type="button"
                              className="review-btn review-btn-reject"
                              disabled={busyId === id}
                              onClick={() => handleAction(item, "confirm")}
                            >
                              {busyId === id ? "Working..." : "Reject"}
                            </button>
                            <button
                              type="button"
                              className="review-btn review-btn-approve"
                              disabled={busyId === id}
                              onClick={() => handleAction(item, "override")}
                            >
                              {busyId === id ? "Working..." : "Approve"}
                            </button>
                          </div>
                          <div className="review-summary-peek">{summary.slice(0, 120)}</div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="ai-empty">No pending review cases.</div>
          )}
        </div>
      </div>

      {selected && (
        <div className="review-modal-overlay" role="dialog" aria-modal="true" onClick={() => setSelected(null)}>
          <div className="review-modal" onClick={(e) => e.stopPropagation()}>
            <div className="review-modal-header">
            <h3>Candidate Final Review</h3>
              <button type="button" className="review-close" onClick={() => setSelected(null)}>
                ✕
              </button>
            </div>
            <div className="review-modal-grid">
              <div><strong>Name:</strong> {`${selected?.firstName || ""} ${selected?.lastName || ""}`.trim() || "--"}</div>
              <div><strong>Phone:</strong> {selected?.phone || "--"}</div>
              <div><strong>Role:</strong> {selected?.jobTitle || selected?.role || "--"}</div>
              <div>
                <strong>Rating:</strong>{" "}
                <RatingBadge item={selected} />
              </div>
              {VIDEO_CONSENT_DECLINE_LABELS[selected?.videoConsentStatus] ? (
                <div>
                  <strong>Consent:</strong>{" "}
                  <VideoConsentDeclineBadge videoConsentStatus={selected?.videoConsentStatus} />
                </div>
              ) : null}
              {VIDEO_ENDED_BY_LABELS[selected?.interview?.endedBy] ? (
                <div>
                  <strong>Interview outcome:</strong>{" "}
                  <VideoEndedByBadge endedBy={selected?.interview?.endedBy} />
                </div>
              ) : null}
            </div>
            <div className="review-modal-summary">
              {String(
                selected?.interview?.summarySnippet ||
                  "No interview summary available.",
              ).slice(0, 300)}
            </div>
            <div className="review-modal-actions">
              <button type="button" className="review-btn review-btn-reject" onClick={() => handleAction(selected, "confirm")}>
                Reject
              </button>
              <button type="button" className="review-btn review-btn-approve" onClick={() => handleAction(selected, "override")}>
                Approve
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={`review-toast review-toast-${toast.type}`}>
          {toast.text}
        </div>
      )}
    </div>
  );
};

export default HiringReviewQueue;
